import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
} from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import {
  EMPTY,
  Observable,
  catchError,
  defer,
  finalize,
  of,
  switchMap,
} from 'rxjs';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { handleErrorMessage } from '../operators/error.operator';
import { CommonService } from './common.service';
import { StatusCodes } from '../utility/constants';

/**
 * Signed URL + blob upload for a single attachment field, with progress.
 *
 * Holds per-field state, so provide it in a component's `providers`, not in root.
 */
@Injectable()
export class AttachmentUploadService {
  private readonly commonService = inject(CommonService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly errorHandler = handleErrorMessage();

  /** null when idle, 0-100 while the signed URL + blob upload are in flight. */
  public progress: number | null = null;

  public get isUploading(): boolean {
    return this.progress !== null;
  }

  /**
   * Fetches the signed URL, starts the blob upload, and emits the signed
   * metadata (fileName / originFileName) for the caller to keep.
   *
   * `form` is disabled for the duration: the popup's Save only closes on a
   * valid form and a disabled group never is, so the row cannot be saved
   * against a file that is still transferring.
   */
  public upload(
    request: { fileName: string; requestType: string },
    file: File,
    form: FormGroup
  ): Observable<any> {
    // defer keeps the busy state tied to subscription: an unsubscribed call
    // would otherwise leave the form disabled with nothing to re-enable it.
    return defer(() => {
      this.progress = 0;
      form.disable({ emitEvent: false });
      return this.commonService.attachmentSignedUrl(request);
    }).pipe(
      this.errorHandler(),
      catchError(() => {
        // handleErrorMessage() has already raised the toast and rethrown.
        this.reset(form);
        return EMPTY;
      }),
      switchMap((response: any) => {
        if (response.statusCode !== StatusCodes.Success) {
          this.reset(form);
          return EMPTY;
        }
        this.putBlob(response.data, file, form);
        return of(response.data);
      })
    );
  }

  /**
   * Deliberately not tied to a DestroyRef. Closing the popup mid-upload would
   * abort the Azure blob PUT and leave the saved row pointing at a missing file.
   *
   * Kept off `handleErrorMessage()` on purpose: this call goes to Azure, not the
   * PacTrak API, so a failure body is XML rather than `{ statusCode, code,
   * message }` and `extractError` would throw reading `error.error.message`.
   */
  private putBlob(signedData: any, file: File, form: FormGroup) {
    this.commonService
      .attachmentStoredSignedUrl(signedData, file, true)
      .pipe(finalize(() => this.reset(form)))
      .subscribe({
        next: (event: HttpEvent<string>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.progress = Math.round((100 * event.loaded) / event.total);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private reset(form: FormGroup) {
    this.progress = null;
    form.enable({ emitEvent: false });
  }
}
